/**
 * API client for the Lesson Planner admin section.
 *
 * Backend: /api/v1/oh_lesson_planner/admin/ (DRF DefaultRouter, same shape as
 * the preschool-evaluation admin API). Every `apiEndpoints.X` reference lives
 * INSIDE a function body (TDZ-safety — mirrors preschoolEvaluation.js / centre.js).
 */
import { isEmpty } from 'lodash'

import {
  apiEndpoints,
  validateDeleteRequest,
  validateUpdateRequest
} from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, patch, post, put, remove } from '../request'

// Generic CRUD factory. The returned async functions reference
// apiEndpoints[endpointKey] only when called, never at module init.
const makeCrud = (endpointKey, shape) => ({
  fetch: async ({ url, queries } = {}) => {
    const requestUrl = !isEmpty(url)
      ? url
      : makeRequestUrl(apiEndpoints[endpointKey], queries)
    const response = await get(requestUrl)
    return response.data
  },
  create: async (data) => {
    // Trailing slash required: the DRF router serves the collection at `base/`,
    // and Django's APPEND_SLASH can't redirect a POST (it would drop the body),
    // so a slash-less POST 500s. Matches `update`'s `base/{uuid}/`.
    const response = await post(`${apiEndpoints[endpointKey]}/`, shape(data))
    return response.data
  },
  update: async (data) => {
    validateUpdateRequest(data)
    const response = await patch(
      `${apiEndpoints[endpointKey]}/${data.uuid}/`,
      shape(data)
    )
    return response.data
  },
  remove: async ({ ids }) => {
    validateDeleteRequest(ids)
    await Promise.all(
      ids.map((id) => remove(`${apiEndpoints[endpointKey]}/${id}/`))
    )
    return ids
  }
})

const num = (v, d = 0) => (v != null && v !== '' ? Number(v) : d)
const arr = (v) => (Array.isArray(v) ? v : [])

// ---- request shapes ----

const gradeGroupShape = (data) => ({
  name: data.name,
  level_code: data.level_code || null,
  standard: data.standard || null,
  emoji: data.emoji || '',
  color: data.color || 'slate',
  display_order: num(data.display_order),
  is_active: data.is_active !== false
})

// `key` is server-generated from the label (read-only) — admins don't set it.
const subjectShape = (data) => ({
  label: data.label,
  icon: data.icon || '',
  color: data.color || 'slate',
  display_order: num(data.display_order),
  is_active: data.is_active !== false
})

const layoutShape = (data) => ({
  key: data.key,
  label: data.label,
  icon: data.icon || '📍',
  is_default: data.is_default === true,
  is_active: data.is_active !== false
})

const gradeSubjectShape = (data) => ({
  grade_group: data.grade_group,
  subject: data.subject
})

const quotaShape = (data) => ({
  grade_group: data.grade_group,
  subject: data.subject,
  weekly_required_count: num(data.weekly_required_count)
})

const activityShape = (data) => ({
  grade_group: data.grade_group,
  subject: data.subject,
  title: data.title,
  outcomes: arr(data.outcomes),
  materials: arr(data.materials),
  steps: arr(data.steps),
  visual_evolution: data.visual_evolution || '',
  month: data.month != null && data.month !== '' ? Number(data.month) : null,
  week: data.week != null && data.week !== '' ? Number(data.week) : null,
  image_urls: arr(data.image_urls),
  is_active: data.is_active !== false
})

const inventoryShape = (data) => ({
  centre: data.centre,
  name: data.name,
  quantity: num(data.quantity, 1)
})

const observationShape = (data) => ({
  plan_cell: data.plan_cell,
  observer_label: data.observer_label || '',
  teacher_name: data.teacher_name || '',
  observation_date: data.observation_date || null,
  env_setup: data.env_setup || {},
  facilitation: data.facilitation || {},
  did_well: data.did_well || '',
  to_improve: data.to_improve || ''
})

// ---- entities ----

const gradeGroup = makeCrud('plannerGradeGroup', gradeGroupShape)
const subject = makeCrud('plannerSubject', subjectShape)
const layout = makeCrud('plannerLayout', layoutShape)
const gradeSubject = makeCrud('plannerGradeSubject', gradeSubjectShape)
const quota = makeCrud('plannerQuota', quotaShape)
const activity = makeCrud('plannerActivity', activityShape)
const inventory = makeCrud('plannerInventory', inventoryShape)
const observation = makeCrud('plannerObservation', observationShape)

export const fetchPlannerGradeGroup = gradeGroup.fetch
export const createPlannerGradeGroup = gradeGroup.create
export const updatePlannerGradeGroup = gradeGroup.update
export const deletePlannerGradeGroup = gradeGroup.remove

export const fetchPlannerSubject = subject.fetch
export const createPlannerSubject = subject.create
export const updatePlannerSubject = subject.update
export const deletePlannerSubject = subject.remove

export const fetchPlannerLayout = layout.fetch
export const createPlannerLayout = layout.create
export const updatePlannerLayout = layout.update
export const deletePlannerLayout = layout.remove

export const fetchPlannerGradeSubject = gradeSubject.fetch
export const createPlannerGradeSubject = gradeSubject.create
export const deletePlannerGradeSubject = gradeSubject.remove

export const fetchPlannerQuota = quota.fetch
export const createPlannerQuota = quota.create
export const updatePlannerQuota = quota.update

export const fetchPlannerActivity = activity.fetch
export const createPlannerActivity = activity.create
export const updatePlannerActivity = activity.update
export const deletePlannerActivity = activity.remove

export const fetchPlannerInventory = inventory.fetch
export const createPlannerInventory = inventory.create
export const updatePlannerInventory = inventory.update
export const deletePlannerInventory = inventory.remove

export const fetchPlannerObservation = observation.fetch
export const createPlannerObservation = observation.create
export const deletePlannerObservation = observation.remove

const timetableShape = (data) => ({
  grade_group: data.grade_group,
  // centre + teacher NULL = the grade's default grid; set = one teacher's grid.
  centre: data.centre || null,
  teacher: data.teacher || null,
  name: data.name,
  display_order: num(data.display_order)
})
const timetable = makeCrud('plannerTimetable', timetableShape)
export const createPlannerTimetable = timetable.create
export const updatePlannerTimetable = timetable.update
export const deletePlannerTimetable = timetable.remove

// ---- non-CRUD endpoints ----

export const fetchPlannerTimetable = async ({ queries } = {}) => {
  const response = await get(
    makeRequestUrl(apiEndpoints.plannerTimetable, queries)
  )
  return response.data
}

// Replace a template's rows. Backend action accepts PUT or PATCH.
export const savePlannerTimetableRows = async (templateUuid, rows) => {
  const response = await patch(
    `${apiEndpoints.plannerTimetable}/${templateUuid}/rows/`,
    { rows }
  )
  return response.data
}

export const fetchPlannerPlans = async ({ queries } = {}) => {
  const response = await get(makeRequestUrl(apiEndpoints.plannerPlans, queries))
  return response.data
}

export const fetchPlannerPlanDetail = async ({ queries } = {}) => {
  const response = await get(
    makeRequestUrl(apiEndpoints.plannerPlanDetail, queries)
  )
  return response.data
}

// Teachers who can own a plan for a (centre, grade) — the admin's teacher picker.
// Each carries a student_count.
export const fetchPlannerPlanTeachers = async ({ queries } = {}) => {
  const response = await get(
    makeRequestUrl(apiEndpoints.plannerPlanTeachers, queries)
  )
  return response.data
}

export const importPlannerCsv = async (csvText) => {
  // Trailing slash required (see makeCrud.create) — slash-less POST 500s.
  const response = await post(`${apiEndpoints.plannerCsvImport}/`, {
    csv: csvText
  })
  return response.data
}

// ---- Org-level program observations ----

const observationProgramShape = (data) => ({
  name: data.name,
  course_match: data.course_match || '',
  display_order: num(data.display_order),
  is_active: data.is_active !== false
})

const observationQuestionShape = (data) => ({
  program: data.program,
  section: data.section || '',
  text: data.text,
  display_order: num(data.display_order),
  is_active: data.is_active !== false
})

const observationProgram = makeCrud(
  'plannerObservationProgram',
  observationProgramShape
)
const observationQuestion = makeCrud(
  'plannerObservationQuestion',
  observationQuestionShape
)

export const createPlannerObservationProgram = observationProgram.create
export const updatePlannerObservationProgram = observationProgram.update
export const deletePlannerObservationProgram = observationProgram.remove

export const createPlannerObservationQuestion = observationQuestion.create
export const updatePlannerObservationQuestion = observationQuestion.update
export const deletePlannerObservationQuestion = observationQuestion.remove

export const fetchPlannerObservationPrograms = async ({ queries } = {}) => {
  const response = await get(
    makeRequestUrl(apiEndpoints.plannerObservationProgram, queries)
  )
  return response.data
}

export const fetchPlannerObservationTeachers = async ({ queries } = {}) => {
  const response = await get(
    makeRequestUrl(apiEndpoints.plannerObservationTeachers, queries)
  )
  return response.data
}

export const fetchPlannerObservationSessions = async ({ queries } = {}) => {
  const response = await get(
    makeRequestUrl(apiEndpoints.plannerObservationSessions, queries)
  )
  return response.data
}

export const fetchPlannerProgramObservations = async ({ queries } = {}) => {
  const response = await get(
    makeRequestUrl(apiEndpoints.plannerProgramObservation, queries)
  )
  return response.data
}

// System-wide list of every observation (program + preschool QOT).
export const fetchPlannerAllObservations = async ({ queries } = {}) => {
  const response = await get(
    makeRequestUrl(apiEndpoints.plannerAllObservations, queries)
  )
  return response.data
}

// ---- Preschool QOT questions (per grade, admin-editable) ----

const qotQuestionShape = (data) => ({
  grade_group: data.grade_group,
  section: data.section,
  title: data.title,
  description: data.description || '',
  display_order: num(data.display_order),
  is_active: data.is_active !== false
})
const qotQuestion = makeCrud('plannerQotQuestion', qotQuestionShape)
export const createPlannerQotQuestion = qotQuestion.create
export const updatePlannerQotQuestion = qotQuestion.update
export const deletePlannerQotQuestion = qotQuestion.remove

export const fetchPlannerQotQuestions = async ({ queries } = {}) => {
  const response = await get(
    makeRequestUrl(apiEndpoints.plannerQotQuestion, queries)
  )
  return response.data
}

// ---- Admin plan editor (the teacher's plan-filling, in Alfred) ----

export const fetchPlannerPlanEditor = async ({ queries } = {}) => {
  const response = await get(
    makeRequestUrl(apiEndpoints.plannerPlanEditor, queries)
  )
  return response.data
}

export const fetchPlannerPlanRepository = async ({ queries } = {}) => {
  const response = await get(
    makeRequestUrl(apiEndpoints.plannerPlanRepository, queries)
  )
  return response.data
}

// Trailing slash required (DRF APPEND_SLASH can't redirect PUT/POST).
export const upsertPlannerPlanCell = async (data) => {
  const response = await put(`${apiEndpoints.plannerPlanCell}/`, data)
  return response.data
}

export const deletePlannerPlanCell = async (data) => {
  const response = await remove(`${apiEndpoints.plannerPlanCell}/`, {}, data)
  return response.data
}

export const setPlannerPlanStatus = async (data) => {
  const response = await post(`${apiEndpoints.plannerPlanStatus}/`, data)
  return response.data
}

// Claim (or switch) a batch's grade timetable. Trailing slash required.
export const claimPlannerTimetable = async (data) => {
  const response = await post(`${apiEndpoints.plannerTimetableClaim}/`, data)
  return response.data
}

export const createPlannerProgramObservation = async (data) => {
  // Trailing slash required (see makeCrud.create) — a slash-less POST hits
  // Django's APPEND_SLASH, which can't redirect a POST, and 500s.
  const response = await post(
    `${apiEndpoints.plannerProgramObservation}/`,
    data
  )
  return response.data
}
